import axios from "axios";
import { IHeaders } from "../interfaces/IHeaders";
import { CONFIG } from "./config";

const BASE = `${CONFIG.API_URL}loyalty`;

// ─── Tipos de referência ──────────────────────────────────────────────────────

export type LoyaltyPlan = "NONE" | "BLUE" | "PLUS" | "ELITE" | "ONE";

export const LOYALTY_PLANS: LoyaltyPlan[] = [
  "NONE",
  "BLUE",
  "PLUS",
  "ELITE",
  "ONE",
];

export type LoyaltyJobStatus = "PROCESSING" | "DONE" | "FAILED";

export type LoyaltyBatchErrorCode =
  | "INVALID_EMAIL"
  | "INVALID_LOYALTY_PLAN"
  | "DUPLICATE_ROW_IN_FILE"
  | "INVALID_DATE_FORMAT"
  | "SAVE_ERROR"
  | "PARSE_ERROR"
  | "EMPTY_FILE";

export interface IPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first?: boolean;
  last?: boolean;
}

export interface IPageParams {
  page?: number;
  size?: number;
  sort?: string;
}

// ─── Response types ───────────────────────────────────────────────────────────

export interface IVwLoyalty {
  id: number;
  loyaltyPlan: LoyaltyPlan;
  active: boolean;
  customerEmail: string;
  countryCode: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface ILoyaltyBatchJob {
  jobId: string;
  groupName?: string;
  status: LoyaltyJobStatus;
  countryCode: string;
  totalRows: number;
  successRows: number;
  failedRows: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ILoyaltyBatchError {
  rowNumber: number;
  errorCode: LoyaltyBatchErrorCode;
  errorMessage: string;
}

export interface ILoyaltyJobAccepted {
  jobId: string;
  statusUrl: string;
}

export interface ILoyaltyReportJob {
  jobId: string;
  status: LoyaltyJobStatus;
  fileName: string;
  downloadUrl?: string;
  createdAt: string;
  createdBy: string;
}

// ─── Request types ────────────────────────────────────────────────────────────
export interface ILoyaltyCreateRequest {
  loyaltyPlan: LoyaltyPlan;
  customerEmail: string;
  countryCode: string;
}

export interface ILoyaltyUpdateRequest {
  loyaltyPlan: LoyaltyPlan;
  customerEmail: string;
  active: boolean;
}

// ─── Params ───────────────────────────────────────────────────────────────────

export interface ILoyaltyListParams extends IPageParams {
  search?: string;
  active?: boolean;
}

export interface ILoyaltyReportParams {
  /** `yyyy-MM-dd` */
  startDate?: string;
  endDate?: string;
  loyaltyPlan?: LoyaltyPlan;
  active?: boolean;
}

const multipartHeaders = ({ headers }: IHeaders, groupName: string): IHeaders => {
  const rest = { ...(headers as any) };
  delete rest["Content-Type"];
  return { headers: { ...rest, groupName } };
};

// ─── Services ───────────────────────────────────────────────────────────────────
export const loyaltyService = {
  /** Lista registros de elegibilidade do país do header, paginados, com filtros opcionais de `search` e `active`. */
  list: (params: ILoyaltyListParams, headers: IHeaders) =>
    axios.get<IPage<IVwLoyalty>>(BASE, { ...headers, params }),

  /** Busca um registro de elegibilidade por ID. Retorna 404 se não existir. */
  getById: (id: number, headers: IHeaders) =>
    axios.get<IVwLoyalty>(`${BASE}/${id}`, headers),

  /** Cria a elegibilidade. Se o par `customerEmail + countryCode` já existir, atualiza só o `loyaltyPlan` (upsert) — `active` é sempre `true` neste fluxo. */
  create: (data: ILoyaltyCreateRequest, headers: IHeaders) =>
    axios.post<IVwLoyalty>(BASE, data, headers),

  /** Atualiza `loyaltyPlan`, `customerEmail` e `active`. Retorna 409 se o e-mail já pertencer a outro registro do mesmo país. */
  update: (id: number, data: ILoyaltyUpdateRequest, headers: IHeaders) =>
    axios.put<IVwLoyalty>(`${BASE}/${id}`, data, headers),

  /** Ativa ou desativa o registro alterando somente o campo `active`. `updatedBy`/`updatedAt` são gravados pelo backend. */
  setActive: (id: number, active: boolean, headers: IHeaders) =>
    axios.patch<IVwLoyalty>(`${BASE}/${id}/active`, { active }, headers),

  // ── Batch import (XLSX) ──

  /** Envia a planilha `.xlsx`. `groupName` é enviado como header e deve ser único por país — retorna 409 se já existir. Processamento assíncrono — retorna 202 com `jobId` para polling. */
  uploadBatch: (file: File, groupName: string, headers: IHeaders) => {
    const formData = new FormData();
    formData.append("file", file);

    return axios.post<ILoyaltyJobAccepted>(
      `${BASE}/batch`,
      formData,
      multipartHeaders(headers, groupName),
    );
  },

  /** Lista os jobs de importação do país do header, ordenados por `createdAt` desc. */
  listBatchJobs: (params: IPageParams, headers: IHeaders) =>
    axios.get<IPage<ILoyaltyBatchJob>>(`${BASE}/batch`, { ...headers, params }),

  /** Status de um job de importação. Consultar a cada 5s enquanto `status === "PROCESSING"`. */
  getBatchJob: (jobId: string, headers: IHeaders) =>
    axios.get<ILoyaltyBatchJob>(`${BASE}/batch/${jobId}`, headers),

  /** Registros de elegibilidade criados/atualizados com sucesso por um job de importação. */
  getBatchParticipants: (
    jobId: string,
    params: IPageParams,
    headers: IHeaders,
  ) =>
    axios.get<IPage<IVwLoyalty>>(`${BASE}/batch/${jobId}/participants`, {
      ...headers,
      params,
    }),

  /** Erros por linha de um job de importação. */
  getBatchErrors: (jobId: string, params: IPageParams, headers: IHeaders) =>
    axios.get<IPage<ILoyaltyBatchError>>(`${BASE}/batch/${jobId}/errors`, {
      ...headers,
      params,
    }),

  // ── Relatório assíncrono ──

  /** Solicita a geração do relatório (202 com `jobId`). Retorna 400 se `endDate` for anterior a `startDate`. */
  requestReport: (params: ILoyaltyReportParams, headers: IHeaders) =>
    axios.get<ILoyaltyJobAccepted>(`${BASE}/report`, { ...headers, params }),

  /** Status do job de relatório. O `downloadUrl` só vem quando `status === "DONE"`. */
  getReportJob: (jobId: string, headers: IHeaders) =>
    axios.get<ILoyaltyReportJob>(`${BASE}/report/${jobId}`, headers),
};
